import axios from 'axios'

const API_BASE = 'https://api.psro.net/api/auth'

interface User {
  id: number
  email: string
  username: string
  nickname: string
  fontSize?: number
  role: 'sysadmin' | 'group_admin' | 'user'
}

interface LoginResponse {
  success: boolean
  data?: {
    user: User
    csrfToken: string
  }
  error?: string
}

interface RegisterResponse {
  success: boolean
  data?: User
  error?: string
}

interface LogoutResponse {
  success: boolean
  error?: string
}

export const register = async (
  email: string,
  username: string,
  nickname: string,
  password: string
): Promise<RegisterResponse> => {
  try {
    const csrfData = await getCsrfToken()
    if (!csrfData?.csrfToken) {
      return { success: false, error: '無法取得 CSRF token' }
    }

    const response = await axios.post(`${API_BASE}/register`, {
      email,
      username,
      nickname,
      password,
    }, {
      headers: {
        'x-csrf-token': csrfData.csrfToken,
      },
    })
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return error.response.data
    }
    return {
      success: false,
      error: '網路錯誤',
    }
  }
}

export const login = async (credential: string, password: string): Promise<LoginResponse> => {
  try {
    const csrfData = await getCsrfToken()
    if (!csrfData?.csrfToken) {
      return { success: false, error: '無法取得 CSRF token' }
    }

    const response = await axios.post(`${API_BASE}/login`, {
      credential,
      password,
    }, {
      withCredentials: true,
      headers: {
        'x-csrf-token': csrfData.csrfToken,
      },
    })
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return error.response.data
    }
    return {
      success: false,
      error: '網路錯誤',
    }
  }
}

export const logout = async (): Promise<LogoutResponse> => {
  try {
    const response = await axios.post(`${API_BASE}/logout`, {}, {
      withCredentials: true,
    })
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return error.response.data
    }
    return {
      success: false,
      error: '網路錯誤',
    }
  }
}

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export const validatePasswordStrength = (password: string): { valid: boolean; error?: string } => {
  if (!password || password.length < 8 || password.length > 20) {
    return { valid: false, error: '密碼長度必須為 8-20 碼' }
  }

  const hasUppercase = /[A-Z]/.test(password)
  const hasNumber = /[0-9]/.test(password)
  const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)

  if (!hasUppercase || !hasNumber || !hasSpecial) {
    return {
      valid: false,
      error: '密碼必須包含至少 1 個大寫字母、1 個數字和 1 個特殊符號',
    }
  }

  return { valid: true }
}

export const checkEmail = async (email: string): Promise<{ exists: boolean }> => {
  try {
    const response = await axios.get(`${API_BASE}/check-email`, {
      params: { email },
    })
    return { exists: response.data.exists }
  } catch (error) {
    return { exists: false }
  }
}

export const checkUsername = async (username: string): Promise<{ exists: boolean }> => {
  try {
    const response = await axios.get(`${API_BASE}/check-username`, {
      params: { username },
    })
    return { exists: response.data.exists }
  } catch (error) {
    return { exists: false }
  }
}

export const checkNickname = async (nickname: string): Promise<{ exists: boolean }> => {
  try {
    const response = await axios.get(`${API_BASE}/check-nickname`, {
      params: { nickname },
    })
    return { exists: response.data.exists }
  } catch (error) {
    return { exists: false }
  }
}

export const updateNickname = async (
  userId: number,
  nickname: string
): Promise<{ success: boolean; data?: { id: number; nickname: string }; error?: string }> => {
  try {
    const response = await axios.put(
      `https://api.psro.net/api/users/${userId}`,
      { nickname },
      { withCredentials: true }
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) return error.response.data
    return { success: false, error: '網路錯誤' }
  }
}

export const changePassword = async (
  userId: number,
  oldPassword: string,
  newPassword: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await axios.post(
      `https://api.psro.net/api/users/${userId}/change-password`,
      { oldPassword, newPassword },
      { withCredentials: true }
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) return error.response.data
    return { success: false, error: '網路錯誤' }
  }
}

export const forgotPassword = async (email: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const csrfData = await getCsrfToken()
    if (!csrfData?.csrfToken) {
      return { success: false, error: '無法取得 CSRF token' }
    }

    const response = await axios.post(`${API_BASE}/forgot-password`, { email }, {
      headers: {
        'x-csrf-token': csrfData.csrfToken,
      },
    })
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) return error.response.data
    return { success: false, error: '網路錯誤' }
  }
}

export const resetPassword = async (
  token: string,
  newPassword: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const csrfData = await getCsrfToken()
    if (!csrfData?.csrfToken) {
      return { success: false, error: '無法取得 CSRF token' }
    }

    const response = await axios.post(`${API_BASE}/reset-password`, { token, newPassword }, {
      headers: {
        'x-csrf-token': csrfData.csrfToken,
      },
    })
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) return error.response.data
    return { success: false, error: '網路錯誤' }
  }
}

export const updateFontSize = async (
  userId: number,
  fontSize: number
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await axios.patch(
      `https://api.psro.net/api/users/${userId}/font-size`,
      { fontSize },
      { withCredentials: true }
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) return error.response.data
    return { success: false, error: '網路錯誤' }
  }
}

// Passkey 相關函數
export const getPasskeyRegistrationOptions = async (): Promise<{ success: boolean; data?: { options: any; challengeId: number }; error?: string }> => {
  try {
    const response = await axios.post(
      'https://api.psro.net/api/passkeys/register/options',
      {},
      { withCredentials: true }
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) return error.response.data
    return { success: false, error: '網路錯誤' }
  }
}

export const verifyPasskeyRegistration = async (
  challengeId: number,
  attestation: any
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await axios.post(
      'https://api.psro.net/api/passkeys/register/verify',
      { challengeId, attestation },
      { withCredentials: true }
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) return error.response.data
    return { success: false, error: '網路錯誤' }
  }
}

export const getCsrfToken = async (): Promise<{ csrfToken: string } | null> => {
  try {
    const response = await axios.get('https://api.psro.net/api/csrf-token', {
      withCredentials: true,
    })
    if (response.data.success && response.data.data?.csrfToken) {
      return response.data.data
    }
    return null
  } catch (error) {
    return null
  }
}

export const getPasskeyAuthOptions = async (): Promise<{
  success: boolean
  data?: { options: any; challengeId: number }
  error?: string
}> => {
  try {
    const csrfData = await getCsrfToken()
    if (!csrfData?.csrfToken) {
      return { success: false, error: '無法取得 CSRF token' }
    }

    const response = await axios.post('https://api.psro.net/api/passkeys/authenticate/options', {}, {
      withCredentials: true,
      headers: {
        'x-csrf-token': csrfData.csrfToken,
      },
    })
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) return error.response.data
    return { success: false, error: '網路錯誤' }
  }
}

export const verifyPasskeyAuth = async (
  challengeId: number,
  assertion: any
): Promise<{
  success: boolean
  data?: { user: User; csrfToken: string }
  error?: string
}> => {
  try {
    const csrfData = await getCsrfToken()
    if (!csrfData?.csrfToken) {
      return { success: false, error: '無法取得 CSRF token' }
    }

    const response = await axios.post('https://api.psro.net/api/passkeys/authenticate/verify', {
      challengeId,
      assertion,
    }, {
      withCredentials: true,
      headers: {
        'x-csrf-token': csrfData.csrfToken,
      },
    })
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) return error.response.data
    return { success: false, error: '網路錯誤' }
  }
}

export const listPasskeys = async (): Promise<{
  success: boolean
  data?: { credentials: Array<{ id: string; name: string; createdAt: string; backupEligible: boolean; transports: string[] }> }
  error?: string
}> => {
  try {
    const response = await axios.get('https://api.psro.net/api/passkeys', {
      withCredentials: true,
    })
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) return error.response.data
    return { success: false, error: '網路錯誤' }
  }
}

export const deletePasskey = async (
  credentialId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await axios.delete(`https://api.psro.net/api/passkeys/${credentialId}`, {
      withCredentials: true,
    })
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) return error.response.data
    return { success: false, error: '網路錯誤' }
  }
}

// 恢復碼相關 API

export const generateRecoveryCodes = async (): Promise<{ success: boolean; data?: { codes: string[] }; error?: string }> => {
  try {
    const response = await axios.post('https://api.psro.net/api/auth/recovery-codes/generate', {}, {
      withCredentials: true,
    })
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) return error.response.data
    return { success: false, error: '網路錯誤' }
  }
}

export const getRecoveryCodeStatus = async (): Promise<{ success: boolean; data?: { total: number; unused: number }; error?: string }> => {
  try {
    const response = await axios.get('https://api.psro.net/api/auth/recovery-codes/status', {
      withCredentials: true,
    })
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) return error.response.data
    return { success: false, error: '網路錯誤' }
  }
}

export const loginWithRecoveryCode = async (
  code: string
): Promise<{
  success: boolean
  data?: { user: User; csrfToken: string }
  error?: string
}> => {
  try {
    const csrfData = await getCsrfToken()
    if (!csrfData?.csrfToken) {
      return { success: false, error: '無法取得 CSRF token' }
    }

    const response = await axios.post('https://api.psro.net/api/auth/login/recovery', {
      code,
    }, {
      withCredentials: true,
      headers: {
        'x-csrf-token': csrfData.csrfToken,
      },
    })
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) return error.response.data
    return { success: false, error: '網路錯誤' }
  }
}
